import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client/workspace';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/components/button';
import { Checkbox } from '@/shared/ui/components/checkbox';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Switch } from '@/shared/ui/components/switch';
import { NotFound } from '@/shared/ui/NotFound';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { WEEKDAYS, type Weekday } from '@/modules/products/passes';
import {
  MVP_VALIDITY_UNITS,
  useCreateMvpDefinition,
  useEditMvpDefinition,
  useDeleteMvpDefinition,
  useMvpDefinition,
  type MvpValidityUnit,
} from './definitions';
import {
  blankOption,
  definitionErrors,
  draftFrom,
  emptyDraft,
  isSavable,
  optionErrors,
  optionInputs,
  optionsOnSale,
  pricePerHour,
  type MvpDefinitionDraft,
  type MvpOptionDraft,
} from './mvpDraft';

type TFunction = ReturnType<typeof useTranslation>['t'];

function saveErrorMessage(t: TFunction, error: unknown): string {
  if (error instanceof ApiError && error.code) {
    const key = `multiVisitPasses.errors.${error.code}`;
    const translated = t(key);
    if (translated !== key) return translated;
  }
  return t('multiVisitPasses.errors.generic');
}

/**
 * The one-page Multi-Visit Pass editor (ADR 0064, variant A of #506): Definition fields on top,
 * every MVP option an inline row below, one Save for the lot, and a live POS tile preview. With
 * no `definitionId` it creates; otherwise it edits, or shows read-only to a view-only user.
 */
export function MvpDefinitionPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const auth = useAuthorization();
  const { venues } = useAuth();
  const { venueId } = useVenueContext();
  const { definitionId } = useParams();
  const creating = !definitionId;

  const scope = { kind: 'venue', venueId } as const;
  const canManage = auth.can('products.pass.manage', scope);
  const canView = auth.canAny(['products.pass.view', 'products.pass.manage'], scope);
  const readOnly = !canManage;
  const currency = venues.find((candidate) => candidate.id === venueId)?.currency ?? '';

  const definitionQuery = useMvpDefinition(venueId, definitionId ?? '', canView && !creating);
  const definition = definitionQuery.data ?? null;
  const createDefinition = useCreateMvpDefinition(venueId);
  const editDefinition = useEditMvpDefinition(venueId, definitionId ?? '');
  const deleteDefinition = useDeleteMvpDefinition(venueId, definitionId ?? '');

  const [draft, setDraft] = useState<MvpDefinitionDraft>(emptyDraft);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (definition) setDraft(draftFrom(definition));
  }, [definition]);

  const backToList = () => navigate(`/loc/${venueId}/products?tab=multi-visit-passes`);

  if (!canView || (creating && !canManage)) return <Forbidden />;
  if (!creating && definitionQuery.isLoading)
    return <p className="p-6 text-muted-foreground">{t('multiVisitPasses.loading')}</p>;
  if (!creating && !definition) return <NotFound />;

  function updateOption(key: string, patch: Partial<MvpOptionDraft>) {
    setDraft((current) => ({
      ...current,
      options: current.options.map((option) =>
        option.key === key ? { ...option, ...patch } : option,
      ),
    }));
  }

  function removeOption(key: string) {
    setDraft((current) => ({
      ...current,
      options: current.options.filter((option) => option.key !== key),
    }));
  }

  async function save() {
    setSubmitted(true);
    if (!isSavable(draft)) return;
    const body = {
      name: draft.name.trim(),
      waiverRequired: draft.waiverRequired,
      options: optionInputs(draft),
    };
    try {
      if (creating) {
        await createDefinition.mutateAsync(body);
        toast.success(t('multiVisitPasses.toast.created'));
      } else {
        await editDefinition.mutateAsync({ ...body, active: draft.active });
        toast.success(t('multiVisitPasses.toast.updated'));
      }
      backToList();
    } catch (caught) {
      toast.error(saveErrorMessage(t, caught));
    }
  }

  async function removeDefinition() {
    if (!definition || !window.confirm(t('multiVisitPasses.actions.confirmDelete'))) return;
    try {
      await deleteDefinition.mutateAsync();
      toast.success(t('multiVisitPasses.toast.deleted'));
      backToList();
    } catch (caught) {
      toast.error(saveErrorMessage(t, caught));
    }
  }

  const headerErrors = submitted ? definitionErrors(draft) : [];
  const onSale = optionsOnSale(draft);
  const title = creating
    ? t('multiVisitPasses.form.createTitle')
    : readOnly
      ? t('multiVisitPasses.form.viewTitle')
      : t('multiVisitPasses.form.editTitle');

  return (
    <section className="grid max-w-6xl gap-6 p-6">
      <div>
        <button
          type="button"
          onClick={backToList}
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('multiVisitPasses.actions.back')}
        </button>
        <h1 className="text-2xl font-semibold">{title}</h1>
      </div>

      <div className="grid gap-4 rounded-lg border p-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div className="grid gap-1.5">
          <Label htmlFor="mvp-name">{t('multiVisitPasses.form.name')}</Label>
          <Input
            id="mvp-name"
            value={draft.name}
            disabled={readOnly}
            placeholder={t('multiVisitPasses.form.namePlaceholder')}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
        </div>
        <Label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={draft.waiverRequired}
            disabled={readOnly}
            onCheckedChange={(checked) => setDraft({ ...draft, waiverRequired: checked === true })}
          />
          {t('multiVisitPasses.form.waiverRequired')}
        </Label>
        {!creating && (
          <Label className="flex items-center gap-2 text-sm">
            <Switch
              checked={draft.active}
              disabled={readOnly}
              onCheckedChange={(active) => setDraft({ ...draft, active })}
            />
            {t('multiVisitPasses.form.active')}
          </Label>
        )}
        {!draft.active && (
          <p className="text-xs text-muted-foreground sm:col-span-3">
            {t('multiVisitPasses.form.inactiveHint')}
          </p>
        )}
      </div>

      <div className="grid gap-2">
        <h2 className="text-lg font-semibold">{t('multiVisitPasses.form.options')}</h2>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('multiVisitPasses.form.optionName')}</th>
                <th className="px-3 py-2 font-medium">{t('multiVisitPasses.form.sold')}</th>
                <th className="px-3 py-2 font-medium">{t('multiVisitPasses.form.hours')}</th>
                <th className="px-3 py-2 font-medium">
                  {t('multiVisitPasses.form.price', { currency })}
                </th>
                <th className="px-3 py-2 font-medium">{t('multiVisitPasses.form.validFor')}</th>
                <th className="px-3 py-2 font-medium">{t('multiVisitPasses.form.usableOn')}</th>
                <th className="px-3 py-2 font-medium">{t('multiVisitPasses.form.onSale')}</th>
                {!readOnly && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {draft.options.map((option, index) => (
                <OptionRow
                  key={option.key}
                  option={option}
                  position={index + 1}
                  errors={submitted ? optionErrors(option, draft.options) : {}}
                  currency={currency}
                  readOnly={readOnly}
                  onChange={(patch) => updateOption(option.key, patch)}
                  onRemove={option.soldCount > 0 ? undefined : () => removeOption(option.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            className="justify-self-start"
            onClick={() => setDraft({ ...draft, options: [...draft.options, blankOption()] })}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t('multiVisitPasses.actions.addOption')}
          </Button>
        )}
      </div>

      <div className="grid gap-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          {t('multiVisitPasses.form.preview')}
        </h2>
        <ul aria-label={t('multiVisitPasses.form.preview')} className="flex flex-wrap gap-3">
          {onSale.map((option) => (
            <li key={option.key}>
              <PosTile option={option} currency={currency} />
            </li>
          ))}
        </ul>
        {onSale.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('multiVisitPasses.form.previewEmpty')}</p>
        )}
      </div>

      {submitted && !isSavable(draft) && (
        <div
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {headerErrors.length > 0
            ? headerErrors.map((key) => <p key={key}>{t(key)}</p>)
            : t('multiVisitPasses.validation.fixErrors')}
        </div>
      )}

      {!readOnly && (
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={save}
            disabled={createDefinition.isPending || editDefinition.isPending}
          >
            {t('multiVisitPasses.actions.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={backToList}>
            {t('multiVisitPasses.actions.cancel')}
          </Button>
          {!creating && draft.options.every((option) => option.soldCount === 0) && (
            <Button
              type="button"
              variant="destructive"
              onClick={removeDefinition}
              disabled={deleteDefinition.isPending}
            >
              {t('multiVisitPasses.actions.deleteDefinition')}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

function OptionRow({
  option,
  position,
  errors,
  currency,
  readOnly,
  onChange,
  onRemove,
}: {
  option: MvpOptionDraft;
  position: number;
  errors: ReturnType<typeof optionErrors>;
  currency: string;
  readOnly: boolean;
  onChange: (patch: Partial<MvpOptionDraft>) => void;
  onRemove?: () => void;
}) {
  const { t } = useTranslation('common');
  const perHour = pricePerHour(option);
  const label = (field: string) => `${t(field)} ${position}`;

  return (
    <tr className={cn('border-b align-top last:border-0', !option.active && 'bg-muted/40')}>
      <td className="px-3 py-2">
        <Input
          aria-label={label('multiVisitPasses.form.optionName')}
          value={option.name}
          disabled={readOnly}
          placeholder={t('multiVisitPasses.form.optionNamePlaceholder')}
          className="min-w-32"
          onChange={(event) => onChange({ name: event.target.value })}
        />
        <FieldError message={errors.name} />
      </td>
      <td className="px-3 py-2">
        <span>{t('multiVisitPasses.form.soldCount', { count: option.soldCount })}</span>
      </td>
      <td className="px-3 py-2">
        <Input
          aria-label={label('multiVisitPasses.form.hours')}
          inputMode="numeric"
          className="w-20"
          value={option.hours}
          disabled={readOnly || option.soldCount > 0}
          onChange={(event) => onChange({ hours: event.target.value })}
        />
        <FieldError message={errors.hours} />
        {option.soldCount > 0 && (
          <p className="mt-1 max-w-40 text-xs text-muted-foreground">
            {t('multiVisitPasses.form.soldHoursHint')}
          </p>
        )}
      </td>
      <td className="px-3 py-2">
        <Input
          aria-label={`${t('multiVisitPasses.form.price', { currency })} ${position}`}
          inputMode="decimal"
          className="w-24"
          value={option.price}
          disabled={readOnly}
          onChange={(event) => onChange({ price: event.target.value })}
        />
        {errors.price ? (
          <FieldError message={errors.price} />
        ) : (
          perHour && (
            <p className="mt-1 text-xs text-muted-foreground">
              {t('multiVisitPasses.form.perHour', { amount: perHour, currency })}
            </p>
          )
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <Input
            aria-label={label('multiVisitPasses.form.validityAmount')}
            inputMode="numeric"
            className="w-16"
            value={option.validityAmount}
            disabled={readOnly}
            onChange={(event) => onChange({ validityAmount: event.target.value })}
          />
          <select
            aria-label={label('multiVisitPasses.form.validityUnit')}
            className="rounded-md border bg-background px-1 text-sm"
            value={option.validityUnit}
            disabled={readOnly}
            onChange={(event) => onChange({ validityUnit: event.target.value as MvpValidityUnit })}
          >
            {MVP_VALIDITY_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {t(`multiVisitPasses.units.${unit}`)}
              </option>
            ))}
          </select>
        </div>
        <FieldError message={errors.validity} />
      </td>
      <td className="px-3 py-2">
        <WeekdayPicker
          value={option.weekdays}
          position={position}
          disabled={readOnly}
          onChange={(weekdays) => onChange({ weekdays })}
        />
        <FieldError message={errors.weekdays} />
      </td>
      <td className="px-3 py-2">
        <Switch
          aria-label={label('multiVisitPasses.form.onSale')}
          checked={option.active}
          disabled={readOnly}
          onCheckedChange={(active) => onChange({ active })}
        />
      </td>
      {!readOnly && (
        <td className="px-3 py-2">
          {onRemove && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              aria-label={label('multiVisitPasses.actions.removeOption')}
              onClick={onRemove}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}

/** "Every day" on = null; off shows the seven day toggles, and no day picked is refused on Save. */
function WeekdayPicker({
  value,
  position,
  disabled,
  onChange,
}: {
  value: Weekday[] | null;
  position: number;
  disabled: boolean;
  onChange: (next: Weekday[] | null) => void;
}) {
  const { t } = useTranslation('common');

  function toggle(day: Weekday) {
    const current = value ?? [];
    onChange(current.includes(day) ? current.filter((d) => d !== day) : [...current, day]);
  }

  return (
    <div className="grid gap-1">
      <Label className="flex items-center gap-2 text-xs">
        <Checkbox
          aria-label={`${t('multiVisitPasses.form.everyDay')} ${position}`}
          checked={value === null}
          disabled={disabled}
          onCheckedChange={(checked) => onChange(checked === true ? null : [])}
        />
        {t('multiVisitPasses.form.everyDay')}
      </Label>
      {value !== null && (
        <div className="flex flex-wrap gap-1">
          {WEEKDAYS.map((day) => {
            const on = value.includes(day);
            return (
              <button
                key={day}
                type="button"
                disabled={disabled}
                aria-pressed={on}
                onClick={() => toggle(day)}
                className={cn(
                  'w-10 rounded-md border px-1.5 py-1 text-xs disabled:opacity-50',
                  on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {t(`products.form.availability.weekday.${day}`)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldError({ message }: { message?: string }) {
  const { t } = useTranslation('common');
  if (!message) return null;
  return <p className="mt-1 max-w-48 text-xs text-destructive">{t(message)}</p>;
}

/** What the cashier's POS tile would look like for this option. */
function PosTile({ option, currency }: { option: MvpOptionDraft; currency: string }) {
  const { t } = useTranslation('common');
  const validityAmount = Number(option.validityAmount);
  const days =
    option.weekdays === null
      ? t('multiVisitPasses.form.everyDay')
      : WEEKDAYS.filter((day) => option.weekdays!.includes(day))
          .map((day) => t(`products.form.availability.weekday.${day}`))
          .join(', ');
  return (
    <div className="grid aspect-square w-40 content-between rounded-lg border border-primary/40 bg-primary/5 p-3 text-left">
      <span className="grid gap-1">
        <span className="line-clamp-2 leading-tight font-semibold">
          {option.name.trim() || t('multiVisitPasses.form.unnamed')}
        </span>
        <span className="text-2xl font-bold">
          {t('multiVisitPasses.form.tileHours', { hours: option.hours || '?' })}
        </span>
        <span className="text-[10px] tracking-wide text-muted-foreground uppercase">
          {Number.isInteger(validityAmount) && validityAmount >= 1
            ? t(`multiVisitPasses.validity.${option.validityUnit}`, { count: validityAmount })
            : ''}
          {days && ` · ${days}`}
        </span>
      </span>
      <span className="text-lg font-bold text-primary">
        {option.price ? `${option.price} ${currency}` : '—'}
      </span>
    </div>
  );
}
